import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  RefreshCw,
  CreditCard,
  Calendar,
  ArrowUpRight,
  Building2,
  CheckCircle,
  Clock,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import MSPLayout from '../components/MSPLayout';
import { getMSPEarnings } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function MSPEarnings() {
  const navigate = useNavigate();
  const { msp } = useAuth();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const response = await getMSPEarnings();
      setEarnings(response.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MSPLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </MSPLayout>
    );
  }

  const summary = earnings?.summary || {};
  const commissions = earnings?.commissions || [];
  const trend = earnings?.trend || [];

  return (
    <MSPLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Earnings</h1>
            <p className="text-slate-400">Track your commission earnings</p>
          </div>
          <Button variant="outline" onClick={fetchEarnings} className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stripe Connect Status */}
        {!earnings?.stripe_connected && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="text-white font-medium">Connect Stripe to receive payouts</p>
                  <p className="text-sm text-slate-400">
                    Your commissions are accruing. Connect Stripe to withdraw.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/msp/settings')} className="bg-amber-500 hover:bg-amber-600">
                Connect Stripe
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Earnings</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    ${summary.total_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-emerald-500">
                <TrendingUp className="w-4 h-4 mr-1" />
                All-time earnings
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">This Month</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    ${summary.current_month?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-slate-400">
                <Calendar className="w-4 h-4 mr-1" />
                Current billing period
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Pending Payout</p>
                  <p className="text-3xl font-bold text-amber-400 mt-1">
                    ${summary.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-slate-400">
                Next payout: 1st of month
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Commission Rate</p>
                  <p className="text-3xl font-bold text-purple-400 mt-1">30%</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-slate-400">
                Of all client subscriptions
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Earnings Trend */}
          <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Earnings Trend</CardTitle>
              <CardDescription className="text-slate-400">
                Last 6 months commission earnings
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trend.length > 0 ? (
                <div className="space-y-4">
                  {trend.map((month, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <span className="text-sm text-slate-400 w-24">{month.month}</span>
                      <div className="flex-1 h-8 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                          style={{
                            width: `${Math.min(100, (month.earnings / 3000) * 100)}%`
                          }}
                        />
                      </div>
                      <span className="text-white font-medium w-24 text-right">
                        ${month.earnings?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  No earnings data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Commission Summary */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <p className="text-sm text-slate-400 mb-1">Your Commission</p>
                <p className="text-4xl font-bold text-white">30%</p>
                <p className="text-sm text-slate-400 mt-2">
                  Of every client subscription payment
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-500">1</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Client pays</p>
                    <p className="text-sm text-slate-400">Monthly subscription fee</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-purple-500">2</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Automatic split</p>
                    <p className="text-sm text-slate-400">70% Clear Defense AI, 30% You</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-emerald-500">3</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Monthly payout</p>
                    <p className="text-sm text-slate-400">Via Stripe Connect</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission History */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Commission History</CardTitle>
              <CardDescription className="text-slate-400">
                Recent commission transactions
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" className="border-slate-700">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {commissions.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No commissions yet</p>
                <p className="text-sm text-slate-500 mt-2">
                  Add clients to start earning commissions
                </p>
                <Button
                  onClick={() => navigate('/msp/clients')}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  Add Your First Client
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Client</TableHead>
                    <TableHead className="text-slate-400">Payment Amount</TableHead>
                    <TableHead className="text-slate-400">Commission (30%)</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.map((commission) => (
                    <TableRow key={commission.id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-500" />
                          </div>
                          <span className="font-medium text-white">{commission.client_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        ${commission.payment_amount?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell className="text-emerald-400 font-medium">
                        +${commission.amount?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          commission.status === 'paid'
                            ? 'bg-emerald-500/10 text-emerald-500'
                            : 'bg-amber-500/10 text-amber-500'
                        }>
                          {commission.status === 'paid' ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Paid
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {commission.created_at
                          ? new Date(commission.created_at).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MSPLayout>
  );
}
